import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useGameDetail, useGroupMembers, useMe, useOraclePredict } from '../api/hooks'
import type {
  GameDetail,
  GameDetailLeaderboardEntry,
  GameDetailParticipant,
  GameDetailPlay,
  GroupMemberWithBlock,
  OraclePrediction,
} from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDate } from '../datetime'
import {
  BackButton,
  GameThumb,
  MeepleAvatar,
  Pill,
  ScorePad,
  ScorePadRow,
  TierBadge,
  TopBar,
} from '../primitives'

export const GameDetailScreen = () => {
  const { id: groupId, gameId } = useParams({ from: '/group/$id/game/$gameId' })
  const navigate = useNavigate()
  const me = useMe()
  const detail = useGameDetail(groupId, gameId)

  if (detail.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (!detail.data) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-2)' }}>Game not found in this group.</div>
      </ScreenShell>
    )
  }

  const d = detail.data
  const myEntry = me.data ? d.leaderboard.find((e) => e.userId === me.data.id) : undefined

  return (
    <ScreenShell>
      <TopBar
        left={
          <BackButton fallback={() => navigate({ to: '/group/$id', params: { id: groupId } })} />
        }
        title={d.game.name}
        sub={`${d.totalPlays} play${d.totalPlays === 1 ? '' : 's'} in this group`}
      />

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Hero detail={d} />

        {myEntry && <YourRating entry={myEntry} />}

        <Leaderboard detail={d} myUserId={me.data?.id} />

        <Oracle groupId={groupId} gameId={gameId} myUserId={me.data?.id} />

        <PlaysList plays={d.plays} totalPlays={d.totalPlays} />

        <Schema detail={d} />
      </div>
    </ScreenShell>
  )
}

const Hero = ({ detail }: { detail: GameDetail }) => {
  const { game, bgg } = detail
  const playerRange =
    game.minPlayers && game.maxPlayers
      ? game.minPlayers === game.maxPlayers
        ? `${game.minPlayers}p`
        : `${game.minPlayers}–${game.maxPlayers} players`
      : null
  const metaParts: string[] = []
  if (playerRange) metaParts.push(playerRange)
  if (game.playingTime) metaParts.push(`${game.playingTime}m`)
  if (bgg?.bestPlayerCount != null) metaParts.push(`best at ${bgg.bestPlayerCount}p`)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <GameThumb label={game.name} imageUrl={game.imageUrl} size={84} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 400,
              fontSize: 24,
              lineHeight: 1.1,
              letterSpacing: -0.3,
            }}
          >
            {game.name}
          </div>
          {metaParts.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4 }}>
              {metaParts.join(' · ')}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <Pill color="felt">
              {detail.totalPlays} play{detail.totalPlays === 1 ? '' : 's'}
            </Pill>
            {bgg?.weight != null && <Pill color="gilt">⚖ {bgg.weight.toFixed(1)}</Pill>}
            {detail.schemaOverride && <Pill color="gilt">schema override</Pill>}
          </div>
          {game.bggId !== null && (
            <a
              href={`https://boardgamegeek.com/boardgame/${game.bggId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: 8,
                fontSize: 12,
                color: 'var(--ink-2)',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              View on BGG →
            </a>
          )}
        </div>
      </div>
      {bgg?.description && <Description text={bgg.description} />}
    </div>
  )
}

const Description = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false)
  const COLLAPSED_LIMIT = 280
  const isLong = text.length > COLLAPSED_LIMIT
  const shown = expanded || !isLong ? text : `${text.slice(0, COLLAPSED_LIMIT).trimEnd()}…`
  return (
    <div className="felt-card" style={{ padding: 12, fontSize: 13, color: 'var(--ink-2)' }}>
      <div className="kicker" style={{ marginBottom: 4 }}>
        from BGG
      </div>
      <div style={{ lineHeight: 1.5 }}>{shown}</div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="btn-ghost"
          style={{ marginTop: 6, padding: '4px 10px', fontSize: 12 }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

const YourRating = ({ entry }: { entry: GameDetailLeaderboardEntry }) => (
  <div
    style={{
      padding: 12,
      background: 'var(--paper-2)',
      border: '1px solid var(--line-soft)',
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    }}
  >
    <div>
      <div className="kicker" style={{ fontSize: 10 }}>
        your rating in this group
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
        {entry.playCount} play{entry.playCount === 1 ? '' : 's'} · {entry.wins} win
        {entry.wins === 1 ? '' : 's'} · {(entry.winPct * 100).toFixed(0)}%
      </div>
    </div>
    {entry.mu !== null ? (
      <TierBadge tier={entry.tier} score={entry.mu} />
    ) : (
      <Pill color="slate">provisional</Pill>
    )}
  </div>
)

const Leaderboard = ({
  detail,
  myUserId,
}: {
  detail: GameDetail
  myUserId: string | undefined
}) => {
  if (!detail.leaderboard.length) {
    return (
      <div>
        <Heading>Leaderboard</Heading>
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 16 }}>
            No plays yet.
          </div>
          <div style={{ marginTop: 4, fontSize: 13 }}>
            Record a play of {detail.game.name} to start the leaderboard.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div>
      <Heading>Leaderboard</Heading>
      <ScorePad>
        {detail.leaderboard.map((e, i) => (
          <ScorePadRow
            key={e.userId}
            style={{ background: e.userId === myUserId ? 'var(--gilt-bg)' : undefined }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                minWidth: 22,
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <MeepleAvatar name={e.displayName} hue={e.avatarHue} shape={e.avatarShape} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{e.displayName}</div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  marginTop: 2,
                  letterSpacing: 0.4,
                }}
              >
                {e.playCount} {e.playCount === 1 ? 'play' : 'plays'} · {e.wins}{' '}
                {e.wins === 1 ? 'win' : 'wins'} · {(e.winPct * 100).toFixed(0)}%
              </div>
            </div>
            {e.mu !== null ? (
              <TierBadge tier={e.tier} score={e.mu} compact />
            ) : (
              <Pill color="slate">prov</Pill>
            )}
          </ScorePadRow>
        ))}
      </ScorePad>
    </div>
  )
}

const PlaysList = ({
  plays,
  totalPlays,
}: {
  plays: GameDetailPlay[]
  totalPlays: number
}) => {
  if (!plays.length) return null
  return (
    <div>
      <Heading>
        {totalPlays > plays.length
          ? `Recent plays · ${plays.length} of ${totalPlays}`
          : `Plays · ${plays.length}`}
      </Heading>
      <ScorePad>
        {plays.map((p) => {
          const winners = p.participants.filter((part) => part.rank === 1)
          return (
            <ScorePadRow key={p.id} style={{ alignItems: 'flex-start', padding: '12px 14px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to="/play/$id"
                  params={{ id: p.id }}
                  style={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: 13,
                    color: 'var(--ink)',
                    textDecoration: 'none',
                  }}
                >
                  {formatDate(p.playedAt)}
                </Link>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  {winnerLabel(winners)}
                  {p.participants.length > 0 ? ` · ${p.participants.length}p` : ''}
                  {p.durationMinutes ? ` · ${p.durationMinutes}m` : ''}
                  {p.location ? ` · ${p.location}` : ''}
                </div>
              </div>
              <span style={{ color: 'var(--ink-3)', alignSelf: 'center' }}>→</span>
            </ScorePadRow>
          )
        })}
      </ScorePad>
    </div>
  )
}

const winnerLabel = (winners: GameDetailParticipant[]): string => {
  if (winners.length === 0) return 'no winner'
  const names = winners.map((w) => w.displayName ?? '(guest)')
  if (winners.length === 1) return `winner: ${names[0]}`
  return `winners: ${names.join(', ')}`
}

const Schema = ({ detail }: { detail: GameDetail }) => {
  const s = detail.effectiveScoreSchema
  const tagCount = s.outcomeTags.length
  const subCount = s.subscores.length
  return (
    <div>
      <Heading>Score schema {detail.schemaOverride && '· override'}</Heading>
      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid var(--line-soft)',
          borderRadius: 6,
          padding: 12,
          fontSize: 13,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <SchemaRow label="Rank" value={s.rankRequired ? 'required' : 'optional'} />
        <SchemaRow
          label="Score"
          value={
            s.scoreField.enabled
              ? `${s.scoreField.label} · ${s.scoreField.type} · ${s.scoreField.higherIsBetter ? '↑' : '↓'}`
              : 'disabled'
          }
        />
        {tagCount > 0 && (
          <SchemaRow
            label="Outcome tags"
            value={
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {s.outcomeTags.map((t) => (
                  <Pill key={t} color="slate">
                    {t}
                  </Pill>
                ))}
              </div>
            }
          />
        )}
        {subCount > 0 && (
          <SchemaRow
            label="Subscores"
            value={
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {s.subscores.map((sub) => (
                  <Pill key={sub.label} color="slate">
                    {sub.label}
                  </Pill>
                ))}
              </div>
            }
          />
        )}
      </div>
    </div>
  )
}

const SchemaRow = ({
  label,
  value,
}: {
  label: string
  value: string | React.ReactNode
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 12,
      paddingBottom: 4,
      borderBottom: '1px dashed var(--paper-edge)',
    }}
  >
    <span className="kicker" style={{ fontSize: 10, paddingTop: 2, flexShrink: 0 }}>
      {label}
    </span>
    <div style={{ textAlign: 'right', maxWidth: '70%' }}>{value}</div>
  </div>
)

const Heading = ({ children }: { children: React.ReactNode }) => (
  <div className="kicker" style={{ marginBottom: 6 }}>
    {children}
  </div>
)

// "The Oracle" — pick a roster from group members, see win-probability
// odds for the next play of this game. Uses each member's per-group
// rating for this game; unrated members fall back to OpenSkill defaults.
// Live-updates as players are toggled.
const Oracle = ({
  groupId,
  gameId,
  myUserId,
}: {
  groupId: string
  gameId: string
  myUserId: string | undefined
}) => {
  const members = useGroupMembers(groupId)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [seeded, setSeeded] = useState(false)

  // Default selection: viewer (if a group member) once members load.
  useEffect(() => {
    if (seeded) return
    if (!members.data || !myUserId) return
    if (members.data.some((m) => m.userId === myUserId)) {
      setSelected(new Set([myUserId]))
    }
    setSeeded(true)
  }, [members.data, myUserId, seeded])

  const userIds = Array.from(selected)
  const prediction = useOraclePredict(gameId, groupId, userIds)

  const toggle = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  return (
    <div
      className="felt-card"
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div>
        <div className="kicker">the oracle</div>
        <div
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: 18,
            color: 'var(--ink)',
          }}
        >
          Who would win, if they played now?
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          Pick the roster. Odds update as you go. Unrated players use defaults (so the Oracle won't
          bet the farm on them).
        </div>
      </div>

      <RosterPicker
        members={members.data}
        loading={members.isLoading}
        selected={selected}
        onToggle={toggle}
      />

      <OraclePanel
        prediction={prediction.data}
        loading={prediction.isFetching}
        selectedCount={selected.size}
      />
    </div>
  )
}

const RosterPicker = ({
  members,
  loading,
  selected,
  onToggle,
}: {
  members: GroupMemberWithBlock[] | undefined
  loading: boolean
  selected: Set<string>
  onToggle: (userId: string) => void
}) => {
  if (loading) return <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading members…</div>
  if (!members?.length) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {members.map((m) => {
        const on = selected.has(m.userId)
        return (
          <button
            key={m.userId}
            type="button"
            onClick={() => onToggle(m.userId)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              border: on ? '1.5px solid var(--felt)' : '1px solid var(--line-soft)',
              borderRadius: 999,
              background: on ? 'var(--felt-bg)' : 'transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-display)',
              fontSize: 12,
              color: 'var(--ink)',
            }}
          >
            <MeepleAvatar name={m.displayName} hue={m.avatarHue} shape={m.avatarShape} size={18} />
            {m.displayName}
          </button>
        )
      })}
    </div>
  )
}

const OraclePanel = ({
  prediction,
  loading,
  selectedCount,
}: {
  prediction: OraclePrediction | undefined
  loading: boolean
  selectedCount: number
}) => {
  if (selectedCount < 2) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
        Pick at least two players to consult the Oracle.
      </div>
    )
  }
  if (loading && !prediction) {
    return <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Reading the bones…</div>
  }
  if (!prediction) return null

  const anyProvisional = prediction.participants.some((p) => p.isProvisional)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {prediction.participants.map((p) => {
        const pct = Math.round(p.winProbability * 1000) / 10
        return (
          <div
            key={p.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'var(--font-mono)',
            }}
          >
            <MeepleAvatar name={p.displayName} hue={p.avatarHue} shape={p.avatarShape} size={20} />
            <div style={{ width: 80, fontSize: 12, fontWeight: 600 }}>{p.displayName}</div>
            <div style={{ flex: 1, position: 'relative', height: 16 }}>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'var(--paper-3)',
                  borderRadius: 3,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pct}%`,
                  background: p.isProvisional ? 'var(--paper-edge)' : 'var(--felt)',
                  borderRadius: 3,
                  transition: 'width 200ms ease',
                }}
              />
            </div>
            <div style={{ width: 48, textAlign: 'right', fontSize: 12, color: 'var(--ink-2)' }}>
              {pct.toFixed(1)}%
            </div>
          </div>
        )
      })}
      {anyProvisional && (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', marginTop: 2 }}>
          Faded bars are provisional players — the Oracle's confidence is low for them.
        </div>
      )}
    </div>
  )
}
